import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Plus, Receipt, Send, CheckCircle2, Trash2, Loader2, Save } from "lucide-react";
import { fmtDate } from "./utils";
import { useConfirm } from "../../components/ConfirmProvider";

/**
 * Per-firm Billing dashboard — combines billing preferences (contact,
 * method, notes) with the firm's invoice history and the renewal flow.
 *
 * Workflow:
 *   1. Sales creates a DRAFT invoice with a start date + period (months or
 *      years). The system auto-calculates the end date.
 *   2. Mark as sent → status becomes "sent" and the invoice can be tracked
 *      for follow-up.
 *   3. Mark as paid → status becomes "paid" AND the firm's
 *      subscription_end_date is extended to MAX(current end, invoice end)
 *      so the firm never loses coverage they've already paid for.
 */
const STATUS_STYLES = {
  draft:    { label: "Draft",   bg: "#F1EFEA", fg: "#4B4642" },
  sent:     { label: "Sent",    bg: "#DBEAFE", fg: "#1E40AF" },
  paid:     { label: "Paid",    bg: "#D1FAE5", fg: "#065F46" },
  overdue:  { label: "Overdue", bg: "#FEE2E2", fg: "#9F1239" },
};

export default function BillingDialog({ open, onOpenChange, workspace, onDone }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [prefs, setPrefs] = useState({
    billing_method: "manual_invoice",
    billing_cycle: "monthly",
    custom_price_cents: null,
    billing_contact_name: "",
    billing_contact_email: "",
    billing_notes: "",
  });
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newInv, setNewInv] = useState({
    period_start: new Date().toISOString().slice(0, 10),
    period_unit: "year",
    period_length: 1,
    amount: "",
    notes: "",
  });
  const confirm = useConfirm();

  const load = useCallback(async () => {
    if (!workspace) return;
    setLoading(true);
    try {
      const [inv, ws] = await Promise.all([
        api.get(`/superadmin/workspaces/${workspace.workspace_id}/invoices`),
        api.get(`/superadmin/workspaces`).then((r) =>
          (r.data.workspaces || []).find((w) => w.workspace_id === workspace.workspace_id)),
      ]);
      setInvoices(inv.data.invoices || []);
      const sub = ws?.subscription || null;
      setSubscription(sub);
      if (sub) {
        setPrefs({
          billing_method: sub.billing_method || "manual_invoice",
          billing_cycle: sub.billing_cycle || "monthly",
          custom_price_cents: sub.custom_price_cents,
          billing_contact_name: sub.billing_contact_name || "",
          billing_contact_email: sub.billing_contact_email || "",
          billing_notes: sub.billing_notes || "",
        });
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [workspace]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const savePrefs = async (e) => {
    e.preventDefault();
    setSavingPrefs(true);
    try {
      await api.patch(`/superadmin/workspaces/${workspace.workspace_id}/billing-prefs`, prefs);
      toast.success("Billing preferences saved");
      onDone && onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingPrefs(false); }
  };

  const createInvoice = async (e) => {
    e.preventDefault();
    const amt = parseFloat(newInv.amount);
    if (!newInv.period_start) return toast.error("Pick a start date");
    if (!(amt >= 0)) return toast.error("Amount must be ≥ 0");
    setCreating(true);
    try {
      await api.post(`/superadmin/workspaces/${workspace.workspace_id}/invoices`, {
        period_start: newInv.period_start,
        period_unit: newInv.period_unit,
        period_length: Math.max(1, Number(newInv.period_length) || 1),
        amount_cents: Math.round(amt * 100),
        currency: "SGD",
        notes: newInv.notes || null,
      });
      toast.success("Draft invoice created");
      setNewInv({
        period_start: new Date().toISOString().slice(0, 10),
        period_unit: "year",
        period_length: 1,
        amount: "",
        notes: "",
      });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setCreating(false); }
  };

  const markSent = async (inv) => {
    try {
      await api.post(`/superadmin/invoices/${inv.invoice_id}/mark-sent`);
      toast.success("Invoice marked as sent");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const markPaid = async (inv) => {
    const ok = await confirm({
      title: `Mark invoice as paid?`,
      message: `This will extend the firm's subscription end date to at least ${fmtDate(inv.period_end)} (never shortens). This action can't be undone.`,
      confirmText: "Mark paid",
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/superadmin/invoices/${inv.invoice_id}/mark-paid`);
      const from = data.extended_workspace_end_from;
      const to = data.extended_workspace_end_to;
      toast.success(
        from === to
          ? `Paid. Subscription end date unchanged (${fmtDate(to)}).`
          : `Paid. Subscription extended to ${fmtDate(to)}.`
      );
      load();
      onDone && onDone();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removeInvoice = async (inv) => {
    const ok = await confirm({
      title: "Delete this draft?",
      message: "Only draft/sent invoices can be deleted. This cannot be undone.",
      confirmText: "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/superadmin/invoices/${inv.invoice_id}`);
      toast.success("Invoice deleted");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="billing-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Billing — {workspace?.name}</DialogTitle>
          <DialogDescription>
            Track invoices and payments. Marking an invoice as paid automatically extends the firm's subscription end date.
          </DialogDescription>
        </DialogHeader>

        {/* Billing preferences */}
        {subscription && (
          <form onSubmit={savePrefs} className="card-soft !p-5 space-y-4">
            <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">Billing preferences</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571] mb-1">Billing method</div>
                <select value={prefs.billing_method}
                  onChange={(e) => setPrefs((s) => ({ ...s, billing_method: e.target.value }))}
                  data-testid="prefs-billing-method"
                  className="input-soft">
                  <option value="manual_invoice">Manual invoice</option>
                  <option value="stripe">Stripe (auto card)</option>
                </select>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571] mb-1">Default billing cycle</div>
                <select value={prefs.billing_cycle}
                  onChange={(e) => setPrefs((s) => ({ ...s, billing_cycle: e.target.value }))}
                  data-testid="prefs-billing-cycle"
                  className="input-soft">
                  <option value="monthly">Monthly</option>
                  <option value="annual">Annual</option>
                </select>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571] mb-1">Billing contact name</div>
                <input value={prefs.billing_contact_name}
                  onChange={(e) => setPrefs((s) => ({ ...s, billing_contact_name: e.target.value }))}
                  data-testid="prefs-contact-name"
                  className="input-soft" placeholder="Jane Tan" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571] mb-1">Billing contact email</div>
                <input type="email" value={prefs.billing_contact_email}
                  onChange={(e) => setPrefs((s) => ({ ...s, billing_contact_email: e.target.value }))}
                  data-testid="prefs-contact-email"
                  className="input-soft" placeholder="jane@studionordic.com" />
              </div>
              <div className="sm:col-span-2">
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571] mb-1">Notes (internal)</div>
                <textarea value={prefs.billing_notes}
                  onChange={(e) => setPrefs((s) => ({ ...s, billing_notes: e.target.value }))}
                  data-testid="prefs-notes"
                  className="input-soft" rows={2} placeholder="Payment terms, PO reference, etc." />
              </div>
            </div>
            <div className="flex justify-end">
              <button type="submit" disabled={savingPrefs} data-testid="prefs-save"
                className="btn-secondary inline-flex items-center gap-2 disabled:opacity-60">
                {savingPrefs ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save preferences
              </button>
            </div>
          </form>
        )}

        {/* Create new invoice */}
        <form onSubmit={createInvoice} className="card-soft !p-5 space-y-3">
          <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">New invoice</div>
          <p className="text-[11px] text-[#7A7571]">
            The end date is auto-calculated from start + period.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571] mb-1">Start date</div>
              <input required type="date" className="input-soft" data-testid="inv-start"
                value={newInv.period_start}
                onChange={(e) => setNewInv((s) => ({ ...s, period_start: e.target.value }))} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571] mb-1">Length</div>
              <input required type="number" min={1} className="input-soft" data-testid="inv-length"
                value={newInv.period_length}
                onChange={(e) => setNewInv((s) => ({ ...s, period_length: Math.max(1, Number(e.target.value) || 1) }))} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571] mb-1">Unit</div>
              <select className="input-soft" data-testid="inv-unit"
                value={newInv.period_unit}
                onChange={(e) => setNewInv((s) => ({ ...s, period_unit: e.target.value }))}>
                <option value="month">Month(s)</option>
                <option value="year">Year(s)</option>
              </select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571] mb-1">Amount (SGD)</div>
              <input required type="number" step="0.01" min={0} className="input-soft" data-testid="inv-amount"
                value={newInv.amount}
                onChange={(e) => setNewInv((s) => ({ ...s, amount: e.target.value }))} />
            </div>
          </div>
          <input className="input-soft" placeholder="Notes (optional — e.g. Annual renewal 2026)"
            data-testid="inv-notes"
            value={newInv.notes}
            onChange={(e) => setNewInv((s) => ({ ...s, notes: e.target.value }))} />
          <button data-testid="inv-create" disabled={creating}
            className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60">
            <Plus size={16} /> {creating ? "Creating…" : "Create draft invoice"}
          </button>
        </form>

        {/* Invoice history */}
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-3">Invoices</div>
          {loading && <div className="text-sm text-[#7A7571] italic text-center py-4">Loading…</div>}
          {!loading && invoices.length === 0 && (
            <div className="text-sm text-[#7A7571] italic py-6 text-center">No invoices yet.</div>
          )}
          <div className="space-y-2">
            {invoices.map((inv) => {
              const style = STATUS_STYLES[inv.status] || STATUS_STYLES.draft;
              return (
                <div key={inv.invoice_id} className="p-4 rounded-2xl bg-[#F7F4EC] space-y-2"
                  data-testid={`invoice-row-${inv.invoice_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <Receipt size={14} className="text-[#E07A5F]" />
                        <div className="font-semibold text-[#2C2A29]">
                          {fmtDate(inv.period_start)} → {fmtDate(inv.period_end)}
                        </div>
                        <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: style.bg, color: style.fg }}
                          data-testid={`invoice-status-${inv.invoice_id}`}>
                          {style.label}
                        </span>
                      </div>
                      <div className="text-xs text-[#7A7571]">
                        {inv.period_length} {inv.period_unit}{inv.period_length > 1 ? "s" : ""}
                        {inv.sent_at && <> · Sent {fmtDate(inv.sent_at)}</>}
                        {inv.paid_at && <> · Paid {fmtDate(inv.paid_at)}</>}
                      </div>
                      {inv.notes && <div className="text-xs text-[#7A7571] mt-1 italic">{inv.notes}</div>}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="font-display text-lg font-bold text-[#2C2A29]">
                        {inv.currency} {(inv.amount_cents / 100).toFixed(2)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#EBE5D9]">
                    {inv.status === "draft" && (
                      <button onClick={() => markSent(inv)} data-testid={`invoice-mark-sent-${inv.invoice_id}`}
                        className="text-xs inline-flex items-center gap-1 text-[#1E40AF] hover:underline">
                        <Send size={12} /> Mark as sent
                      </button>
                    )}
                    {inv.status !== "paid" && (
                      <button onClick={() => markPaid(inv)} data-testid={`invoice-mark-paid-${inv.invoice_id}`}
                        className="text-xs inline-flex items-center gap-1 text-[#065F46] hover:underline">
                        <CheckCircle2 size={12} /> Mark as paid
                      </button>
                    )}
                    {inv.status !== "paid" && (
                      <button onClick={() => removeInvoice(inv)} data-testid={`invoice-delete-${inv.invoice_id}`}
                        className="text-xs inline-flex items-center gap-1 text-[#7A7571] hover:text-[#9F1239]">
                        <Trash2 size={12} /> Delete
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
